import axios from 'axios';
import { authenticatedFetch } from '../utils/authenticatedFetch';

// Always use relative URL to leverage Vercel's rewrites
const API_BASE_URL = '/api';

// Utility function to convert base64 content to blob URL
const convertBase64ToBlob = (base64Content, mimeType) => {
  try {
    const binaryString = atob(base64Content);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    const blob = new Blob([bytes], { type: mimeType });
    return {
      blob,
      blobUrl: window.URL.createObjectURL(blob)
    };
  } catch (error) {
    console.error('Error converting base64 to blob:', error);
    return null;
  }
};

export const getAllTemplates = async (school) => {
  try {
    const response = await authenticatedFetch(`${API_BASE_URL}/get-all-templates`, {
      method: 'POST',
      body: JSON.stringify({
        school
      }),
    });
    
    if (!response.ok) {
      throw new Error(`API request failed with status ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error fetching templates:', error);
    throw error;
  }
};

export const startAssessmentByProgram = async (programName, school) => {
  try {
    const response = await axios.post(`${API_BASE_URL}/start-assessment-by-program`, {
      program_name: programName,
      school
    });
    return response.data;
  } catch (error) {
    console.error('Error starting program assessment:', error);
    throw error;
  }
};

export const getAllPrograms = async (school) => {
  try {
    const response = await authenticatedFetch(`${API_BASE_URL}/get-all-programs`, {
      method: 'POST',
      body: JSON.stringify({
        school
      }),
    });
    
    if (!response.ok) {
      throw new Error(`API request failed with status ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error fetching programs:', error);
    throw error;
  }
};

export const getAlignmentScore = async (template_id, details, school = 'UIUC', program = 'BSM') => {
  try {
    const response = await authenticatedFetch(`${API_BASE_URL}/get-alignment-score-backup`, {
      method: 'POST',
      body: JSON.stringify({
        template_id,
        details,
        school,
        program
      }),
    });
    
    if (!response.ok) {
      throw new Error(`API request failed with status ${response.status}`);
    }
    
    const data = await response.json();
    
    // The backend now returns { alignmentData: ..., indirectScores: ... }
    // If it's the new format, return as-is
    if (data && data.alignmentData !== undefined && data.indirectScores !== undefined) {
      return data;
    }
    
    // If it's the old format, wrap it in the new structure
    return {
      alignmentData: data,
      indirectScores: {}
    };
  } catch (error) {
    console.error('Error fetching alignment score:', error);
    throw error;
  }
}; 

export const getAllCourses = async (school, program_name) => {
  try {
    const response = await axios.post(`${API_BASE_URL}/get-all-courses`, {
      school,
      program_name
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching courses:', error);
    throw error;
  }
};

export const getCredentials = async (courseCodes) => {
  try {
    const response = await axios.post(`${API_BASE_URL}/get-credentials`, {
      course_codes: courseCodes
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching credentials:', error);
    throw error;
  }
};



export const insertMultipleCredentials = async (school, credentialMapping) => {
  try {
    const response = await axios.post(`${API_BASE_URL}/insert-credential`, {
      school: school,
      credential_mapping: credentialMapping
    });
    return response.data;
  } catch (error) {
    console.error('Error inserting multiple credentials:', error);
    throw error;
  }
}; 

export const deleteTemplate = async (templateId) => {
  try {
    const response = await authenticatedFetch(`${API_BASE_URL}/delete-template`, {
      method: 'POST',
      body: JSON.stringify({
        template_id: templateId
      }),
    });
    
    if (!response.ok) {
      throw new Error(`API request failed with status ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error deleting template:', error);
    throw error;
  }
}; 

export const getRecommendations = async (template_id, details) => {
  try {
    const response = await authenticatedFetch(`${API_BASE_URL}/get-recommendation`, {
      method: 'POST',
      body: JSON.stringify({
        template_id,
        details
      }),
    });
    
    if (!response.ok) {
      throw new Error(`API request failed with status ${response.status}`);
    }
    
    /**
     * Expected response format:
     * {
     *   "COURSE-ID1": {
     *     "overall_score": { "1": 38.22, "2": 37.53 },
     *     "full_recommendation": { "1": "markdown text...", "2": "markdown text..." },
     *     "concise_recommendation": { "1": "text summary...", "2": "text summary..." }
     *   },
     *   "COURSE-ID2": {
     *     ...
     *   }
     * }
     */
    return await response.json();
  } catch (error) {
    console.error('Error fetching recommendations:', error);
    throw error;
  }
};

export const getRecommendedCourseDetails = async (templateId, programName) => {
  try {
    const response = await authenticatedFetch(`${API_BASE_URL}/get-recommended-course-details`, {
      method: 'POST',
      body: JSON.stringify({ 
        template_id: templateId, 
        program_name: programName 
      })
    });

    if (!response.ok) {
      throw new Error(`API request failed with status ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching recommended course details:', error);
    throw error;
  }
};

export const getLearningGoalsData = async (template_id, course_list) => {
  try {
    const response = await authenticatedFetch(`${API_BASE_URL}/get-learning-goals-data`, {
      method: 'POST',
      body: JSON.stringify({
        template_id,
        course_list
      }),
    });
    
    if (!response.ok) {
      throw new Error(`API request failed with status ${response.status}`);
    }
    
    /**
     * Expected response format:
     * {
     *   "BSM-BADM310": {
     *     "LG1": {
     *       "description": "...",
     *       "optimized_description": "..." | null,
     *       "original_score": 78.23,
     *       "optimized_score": 85.45 | null
     *     },
     *     ...
     *   },
     *   ...
     * }
     */
    return await response.json();
  } catch (error) {
    console.error('Error fetching learning goals data:', error);
    throw error;
  }
}; 

export const updateTemplateName = async (templateId, newName) => {
  try {
    const response = await authenticatedFetch(`${API_BASE_URL}/update-template-name`, {
      method: 'POST',
      body: JSON.stringify({
        template_id: templateId,
        new_template_name: newName
      }),
    });
    
    if (!response.ok) {
      throw new Error(`API request failed with status ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error updating template name:', error);
    throw error;
  }
}; 

export const exportExcelReport = async (template_id, details) => {
  try {
    const response = await authenticatedFetch(`${API_BASE_URL}/export-excel-report`, {
      method: 'POST',
      body: JSON.stringify({
        template_id,
        details
      }),
    });
    
    if (!response.ok) {
      throw new Error(`API request failed with status ${response.status}`);
    }
    
    const data = await response.json();
    
    // Debug logging to understand the response structure
    console.log('Export Excel API response:', data);
    
    // Handle new base64 response format
    if (data && data.status === 'success' && data.files) {
      // Convert base64 content to blob URL for compatibility
      const files = data.files;
      let downloadUrl = null;
      
      // Prefer Excel file, then PDF, then Word
      if (files.excel && files.excel.content) {
        const result = convertBase64ToBlob(files.excel.content, files.excel.type);
        if (result) downloadUrl = result.blobUrl;
      } else if (files.pdf && files.pdf.content) {
        const result = convertBase64ToBlob(files.pdf.content, files.pdf.type);
        if (result) downloadUrl = result.blobUrl;
      } else if (files.word && files.word.content) {
        const result = convertBase64ToBlob(files.word.content, files.word.type);
        if (result) downloadUrl = result.blobUrl;
      }
      
      if (downloadUrl) {
        return { download_link: downloadUrl };
      } else {
        throw new Error('No valid file content found in response');
      }
    }
    
    // Handle legacy response format with direct download_link
    if (data && data.download_link) {
      // If download_link is an object, try to extract URL from it
      if (typeof data.download_link === 'object') {
        console.warn('download_link is an object:', data.download_link);
        // Try common properties that might contain the URL
        const possibleUrl = data.download_link.url || 
                           data.download_link.link || 
                           data.download_link.href || 
                           JSON.stringify(data.download_link);
        data.download_link = possibleUrl;
      }
      return data;
    }
    
    // If no recognized format, return as-is but log warning
    console.warn('Unexpected response format from export API:', data);
    return data;
    
  } catch (error) {
    console.error('Error exporting Excel report:', error);
    throw error;
  }
}; 

export const getEvaluateResult = async (template_id_list, course_list, school) => {
  try {
    const response = await authenticatedFetch(`${API_BASE_URL}/get-evaluate-result`, {
      method: 'POST',
      body: JSON.stringify({
        template_id_list,
        course_list,
        school
      }),
    });
    
    if (!response.ok) {
      throw new Error(`API request failed with status ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error fetching evaluate result:', error);
    throw error;
  }
};

export const startAssessmentByTemplate = async (school, course_code, template_id, program_name) => {
  try {
    const response = await authenticatedFetch(`${API_BASE_URL}/start-assessment-by-template`, {
      method: 'POST',
      body: JSON.stringify({
        school,
        course_code: Array.isArray(course_code) ? course_code : [course_code],
        template_id,
        program_name
      }),
    });
    
    if (!response.ok) {
      throw new Error(`API request failed with status ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error starting assessment by template:', error);
    throw error;
  }
};

export const getSurveyAnalyses = async (school) => {
  try {
    const response = await authenticatedFetch(`${API_BASE_URL}/surveys/analyses`, {
      method: 'POST',
      body: JSON.stringify({
        school
      }),
    });
    
    if (!response.ok) {
      throw new Error(`API request failed with status ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error fetching survey analyses:', error);
    throw error;
  }
};

// =============================================================================
// API KEYS CONFIGURATION - DIRECT FRONTEND CALLS
// =============================================================================
// 
// IMPORTANT: Add your API keys here for direct frontend calls
// Note: This exposes keys in the frontend, only use for development/testing
// For production, consider using a backend or environment variables
// =============================================================================

const API_KEYS = {
  OPENAI_API_KEY: 'sk-proj-1234567890abcdef1234567890abcdef', // Replace with your actual OpenAI API key
  FIRECRAWL_API_KEY: 'fc-1234567890abcdef1234567890abcdef' // Replace with your actual Firecrawl API key
};

// Direct call to Firecrawl API
export const crawlWithFirecrawl = async (url) => {
  try {
    if (API_KEYS.FIRECRAWL_API_KEY === 'your-firecrawl-api-key-here') {
      throw new Error('Please configure your Firecrawl API key in src/services/api.js');
    }

    const response = await fetch('https://api.firecrawl.dev/v0/scrape', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${API_KEYS.FIRECRAWL_API_KEY}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        url: url,
        pageOptions: {
          onlyMainContent: true
        }
      }),
    });
    
    if (!response.ok) {
      throw new Error(`Firecrawl API error: ${response.status}`);
    }
    
    const data = await response.json();
    
    if (!data.success) {
      throw new Error('Failed to crawl the webpage');
    }
    
    return {
      content: data.data.content || data.data.markdown || '',
      url: url,
      success: true
    };
  } catch (error) {
    console.error('Error crawling URL:', error);
    throw new Error(`Failed to crawl webpage: ${error.message}`);
  }
};

// Direct call to OpenAI API
export const extractLearningGoalsWithAI = async (content) => {
  try {
    if (API_KEYS.OPENAI_API_KEY === 'your-openai-api-key-here') {
      throw new Error('Please configure your OpenAI API key in src/services/api.js');
    }

    const prompt = `
Extract learning goals from the following content. Return ONLY a valid JSON object with this exact structure:

{
  "learning_goals": [
    {
      "main_goal": "Primary learning objective description",
      "sub_goals": ["Sub-objective 1", "Sub-objective 2"]
    }
  ]
}

Content to analyze:
${content}

Instructions:
- Extract clear, specific learning objectives
- Group related sub-objectives under main goals
- Use clear, actionable language
- Return only the JSON object, no other text
`;

    const response = await fetch('https://api.openai.com/v1/chat/completions', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${API_KEYS.OPENAI_API_KEY}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        model: 'gpt-4o-mini',
        messages: [
          {
            role: 'user',
            content: prompt
          }
        ],
        temperature: 0.3,
        max_tokens: 1500
      }),
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(`OpenAI API error: ${errorData.error?.message || response.status}`);
    }
    
    const data = await response.json();
    const aiResponse = data.choices[0].message.content.trim();
    
    // Try to parse the JSON response
    try {
      const parsedResponse = JSON.parse(aiResponse);
      return parsedResponse;
    } catch (parseError) {
      // If parsing fails, try to extract JSON from the response
      const jsonMatch = aiResponse.match(/\{[\s\S]*\}/);
      if (jsonMatch) {
        return JSON.parse(jsonMatch[0]);
      }
      throw new Error('Invalid response format from AI');
    }
  } catch (error) {
    console.error('Error extracting learning goals:', error);
    throw new Error(`Failed to extract learning goals: ${error.message}`);
  }
};

// Validate URL format
export const isValidUrl = (string) => {
  try {
    new URL(string);
    return true;
  } catch (_) {
    return false;
  }
};

// Format extracted learning goals data
export const formatExtractedGoals = (extractedData) => {
  const formattedGoals = [];
  let mainGoalCounter = 1;
  
  if (extractedData.learning_goals && Array.isArray(extractedData.learning_goals)) {
    extractedData.learning_goals.forEach((goal) => {
      // Add main learning goal
      formattedGoals.push({
        id: `LG${mainGoalCounter}`,
        type: 'main',
        description: goal.main_goal || goal.description || ''
      });
      
      // Add sub learning goals if they exist
      if (goal.sub_goals && Array.isArray(goal.sub_goals)) {
        goal.sub_goals.forEach((subGoal, index) => {
          formattedGoals.push({
            id: `LG${mainGoalCounter}.${index + 1}`,
            type: 'sub',
            description: subGoal.description || subGoal
          });
        });
      }
      
      mainGoalCounter++;
    });
  }
  
  // If no goals were extracted, return default
  if (formattedGoals.length === 0) {
    return [{ id: 'LG1', type: 'main', description: '' }];
  }
  
  return formattedGoals;
}; 

// =============================================================================
// MICRO-CREDENTIALS API FUNCTIONS
// =============================================================================

// Get selected credentials for a school
export const getSelectedCredentials = async (school, program_name, course_name) => {
  try {
    const requestBody = {
      school
    };
    
    // Add either program_name or course_name based on what's provided
    if (course_name) {
      requestBody.course_name = course_name;
    } else if (program_name) {
      requestBody.program_name = program_name;
    }
    
    const response = await authenticatedFetch(`${API_BASE_URL}/get-selected-credentials`, {
      method: 'POST',
      body: JSON.stringify(requestBody),
    });
    
    if (!response.ok) {
      throw new Error(`API request failed with status ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error fetching selected credentials:', error);
    throw error;
  }
};

// Delete selected credentials
export const deleteSelectedCredentials = async (school, credentialName) => {
  try {
    const response = await authenticatedFetch(`${API_BASE_URL}/delete-selected-credentials`, {
      method: 'POST',
      body: JSON.stringify({
        school,
        credential_name: credentialName
      }),
    });
    
    if (!response.ok) {
      throw new Error(`API request failed with status ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error deleting selected credentials:', error);
    throw error;
  }
};

// =============================================================================
// ADD PROGRAM COURSES API FUNCTIONS
// =============================================================================

// Upload files for course extraction
export const uploadFilesForCourseExtraction = async (formData) => {
  try {
    const response = await authenticatedFetch(`${API_BASE_URL}/upload-files`, {
      method: 'POST',
      body: formData,
      headers: {} // Let browser set Content-Type for multipart/form-data
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Upload failed: ${response.status} - ${errorText}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error uploading files:', error);
    throw error;
  }
};

// Poll Redis for processing results
export const pollProcessingResult = async (processingId) => {
  try {
    const response = await fetch('https://communal-locust-56899.upstash.io/get/' + processingId, {
      headers: {
        'Authorization': 'Bearer At5DAAIgcDGz2M3fkHsZg3a7e0_k_VwMTpg6Ad0zlbuw9Kr3UsPZYA'
      }
    });
    
    if (!response.ok) {
      throw new Error(`Redis polling failed: ${response.status} - ${response.statusText}`);
    }
    
    const data = await response.json();
    return data.result;
  } catch (error) {
    console.error('Error polling Redis:', error);
    throw error;
  }
};

// Create program with confirmed data
export const createProgram = async (programName, school, courseCodes) => {
  try {
    const myHeaders = new Headers();
    myHeaders.append("Content-Type", "application/json");

    const raw = JSON.stringify({
      "program_name": programName,
      "school": school,
      "course_codes": courseCodes
    });

    const requestOptions = {
      method: "POST",
      headers: myHeaders,
      body: raw,
      redirect: "follow"
    };

    const response = await authenticatedFetch(`${API_BASE_URL}/create-program`, {
      method: "POST",
      headers: myHeaders,
      body: raw,
      redirect: "follow"
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Program creation failed: ${response.status} - ${errorText}`);
    }
    
    return await response.text();
  } catch (error) {
    console.error('Error creating program:', error);
    throw error;
  }
};

// Delete program
export const deleteProgram = async (programId) => {
  try {
    const myHeaders = new Headers();
    myHeaders.append("Content-Type", "application/json");

    const raw = JSON.stringify({
      "program_id": programId
    });

    const requestOptions = {
      method: "POST",
      headers: myHeaders,
      body: raw,
      redirect: "follow"
    };

    const response = await authenticatedFetch(`${API_BASE_URL}/delete-program`, {
      method: "POST",
      headers: myHeaders,
      body: raw,
      redirect: "follow"
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Program deletion failed: ${response.status} - ${errorText}`);
    }
    
    return await response.text();
  } catch (error) {
    console.error('Error deleting program:', error);
    throw error;
  }
};

export const generatePdfReport = async (payload, files = null, onProgress = null, abortSignal = null) => {
  try {
    // Create FormData to send files
    const formData = new FormData();
    
    // Add program information
    formData.append('template_id', payload.template_id);
    formData.append('program_name', payload.program_name);
    formData.append('school_name', payload.school_name);
    formData.append('sections_data', JSON.stringify(payload.sections_data));
    
    // Add files - get all unique files from sections_data
    const allFileNames = new Set();
    payload.sections_data.forEach(section => {
      section.files.forEach(fileName => {
        allFileNames.add(fileName);
      });
    });
    
    // Add actual file objects if provided
    if (files) {
      const fileMap = new Map();
      files.forEach(file => {
        fileMap.set(file.name, file);
      });
      
      Array.from(allFileNames).forEach((fileName, index) => {
        const file = fileMap.get(fileName);
        if (file) {
          formData.append(`pdf_file`, file);
        }
      });
    } else {
      // Fallback: just send file names
      Array.from(allFileNames).forEach((fileName, index) => {
        formData.append(`pdf_file`, fileName);
      });
    }
    
    console.log('Starting PDF generation task...');
    
    // Step 1: Start the task
    const startResponse = await authenticatedFetch(`${API_BASE_URL}/generate-pdf-report`, {
      method: 'POST',
      body: formData,
      signal: abortSignal,
      headers: {} // Let browser set Content-Type for multipart/form-data
    });
    
    if (!startResponse.ok) {
      throw new Error(`Failed to start PDF generation with status ${startResponse.status}`);
    }
    
    const { task_id } = await startResponse.json();
    console.log('PDF generation task started with ID:', task_id);
    
    if (onProgress) {
      onProgress({ status: 'started', task_id, message: 'Task started successfully' });
    }
    
    // Step 2: Poll for status with timeout (5 minutes)
    const maxAttempts = 60; // 5 minutes with 5-second intervals
    let attempts = 0;
    
    const pollStatus = async () => {
      attempts++;
      
      if (attempts > maxAttempts) {
        throw new Error('PDF generation timed out after 5 minutes');
      }
      
      // Check if cancelled
      if (abortSignal && abortSignal.aborted) {
        throw new Error('PDF generation was cancelled');
      }
      
      try {
        const statusResponse = await authenticatedFetch(`${API_BASE_URL}/check-pdf-status/${task_id}`, {
          signal: abortSignal
        });
        
        if (!statusResponse.ok) {
          throw new Error(`Status check failed with status ${statusResponse.status}`);
        }
        
        const statusData = await statusResponse.json();
        console.log('Status check result:', statusData);
        console.log('Files structure:', statusData.files);
        console.log('PDF file:', statusData.files?.pdf);
        console.log('Word file:', statusData.files?.word);
        
        if (onProgress) {
          onProgress({ 
            status: statusData.status, 
            task_id, 
            message: statusData.message || `Status: ${statusData.status}`
          });
        }
        
        if (statusData.status === 'completed') {
          console.log('PDF generation completed successfully');
          
          // Process the completed files
          const processedFiles = {};
          
          if (statusData.files) {
            console.log('Processing files with structure:', statusData.files);
            
            // Handle base64 response format
            if (statusData.files.pdf && statusData.files.pdf.content) {
              const pdfResult = convertBase64ToBlob(statusData.files.pdf.content, statusData.files.pdf.type);
              if (pdfResult) {
                processedFiles.pdf = {
                  blobUrl: pdfResult.blobUrl,
                  blob: pdfResult.blob,
                  filename: statusData.files.pdf.filename || `${payload.program_name}_Report.pdf`,
                  type: statusData.files.pdf.type
                };
              }
            }
            
            if (statusData.files.word && statusData.files.word.content) {
              const wordResult = convertBase64ToBlob(statusData.files.word.content, statusData.files.word.type);
              if (wordResult) {
                processedFiles.word = {
                  blobUrl: wordResult.blobUrl,
                  blob: wordResult.blob,
                  filename: statusData.files.word.filename || `${payload.program_name}_Report.docx`,
                  type: statusData.files.word.type
                };
              }
            }
            
            // Handle S3 URL format with url, type, and filename properties
            if (statusData.files.pdf && statusData.files.pdf.url) {
              console.log('Processing PDF with URL:', statusData.files.pdf.url);
              processedFiles.pdf = {
                blobUrl: statusData.files.pdf.url,
                filename: statusData.files.pdf.filename || `${payload.program_name}_Report.pdf`,
                type: statusData.files.pdf.type || 'application/pdf'
              };
            }
            
            if (statusData.files.word && statusData.files.word.url) {
              console.log('Processing Word with URL:', statusData.files.word.url);
              processedFiles.word = {
                blobUrl: statusData.files.word.url,
                filename: statusData.files.word.filename || `${payload.program_name}_Report.docx`,
                type: statusData.files.word.type || 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
              };
            }
            
            // Handle legacy URL format (fallback)
            if (statusData.files.pdf && !statusData.files.pdf.content && !statusData.files.pdf.url && !processedFiles.pdf) {
              console.log('Attempting to fetch PDF from legacy URL:', statusData.files.pdf);
              try {
                const pdfResponse = await fetch(statusData.files.pdf, { signal: abortSignal });
                const pdfBlob = await pdfResponse.blob();
                processedFiles.pdf = {
                  blobUrl: window.URL.createObjectURL(pdfBlob),
                  blob: pdfBlob,
                  filename: `${payload.program_name}_Report.pdf`,
                  type: 'application/pdf'
                };
              } catch (error) {
                console.warn('Failed to fetch PDF file:', error);
                processedFiles.pdf = null;
              }
            }
            
            if (statusData.files.word && !statusData.files.word.content && !statusData.files.word.url && !processedFiles.word) {
              console.log('Attempting to fetch Word from legacy URL:', statusData.files.word);
              try {
                const wordResponse = await fetch(statusData.files.word, { signal: abortSignal });
                const wordBlob = await wordResponse.blob();
                processedFiles.word = {
                  blobUrl: window.URL.createObjectURL(wordBlob),
                  blob: wordBlob,
                  filename: `${payload.program_name}_Report.docx`,
                  type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
                };
              } catch (error) {
                console.warn('Failed to fetch Word file:', error);
                processedFiles.word = null;
              }
            }
          }
          
          console.log('Processed files:', processedFiles);
          
          return {
            files: processedFiles,
            pdfError: statusData.pdfError || null
          };
          
        } else if (statusData.status === 'failed') {
          throw new Error(statusData.error || 'PDF generation failed');
        } else {
          // Continue polling - wait 5 seconds before next check
          await new Promise(resolve => setTimeout(resolve, 5000));
          return pollStatus();
        }
        
      } catch (error) {
        console.error('Error during status polling:', error);
        throw error;
      }
    };
    
    // Start polling
    return await pollStatus();
    
  } catch (error) {
    console.error('Error generating PDF report:', error);
    
    // Handle specific error types
    if (error.message.includes('timed out after 5 minutes')) {
      throw new Error('PDF generation timed out after 5 minutes. Please try again.');
    } else if (error.message.includes('502')) {
      throw new Error('Server temporarily unavailable. Please try again in a few minutes.');
    } else if (error.message.includes('timeout')) {
      throw new Error('Request timed out. Please check your connection and try again.');
    } else if (error.message.includes('cancelled')) {
      throw new Error('PDF generation was cancelled.');
    }
    
    throw error;
  }
};

// Update template content
export const updateTemplateContent = async (school, updates) => {
  try {
    const response = await authenticatedFetch(`${API_BASE_URL}/update-template-content`, {
      method: 'POST',
      body: JSON.stringify({
        school,
        updates
      }),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error updating template content:', error);
    throw error;
  }
};

// Get career evaluation results
export const getCareerEvaluationResult = async (program_name, school) => {
  try {
    const response = await authenticatedFetch(`${API_BASE_URL}/get-career-evaluation-result`, {
      method: 'POST',
      body: JSON.stringify({
        program_name,
        school
      }),
    });

    if (!response.ok) {
      throw new Error(`API request failed with status ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching career evaluation result:', error);
    throw error;
  }
};

// Get potential courses from Course and SelectedCourse tables
export const getPotentialCourses = async (schoolName, programName) => {
  try {
    const response = await authenticatedFetch(`${API_BASE_URL}/get-potential-courses`, {
      method: 'POST',
      body: JSON.stringify({
        school_name: schoolName,
        program_name: programName
      }),
    });

    if (!response.ok) {
      throw new Error(`API request failed with status ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching potential courses:', error);
    throw error;
  }
};

// Insert potential course into SelectedCourse table
export const insertPotentialCourse = async (courseID, courseType) => {
  try {
    const response = await authenticatedFetch(`${API_BASE_URL}/insert-potential-course`, {
      method: 'POST',
      body: JSON.stringify({
        course_id: courseID,
        course_type: courseType
      }),
    });

    if (!response.ok) {
      throw new Error(`API request failed with status ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error inserting potential course:', error);
    throw error;
  }
};

export const customizeSyllabus = async (syllabusData) => {
  try {
    const response = await authenticatedFetch(`${API_BASE_URL}/customize-syllabus`, {
      method: 'POST',
      body: JSON.stringify(syllabusData),
    });

    if (!response.ok) {
      throw new Error(`API request failed with status ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error customizing syllabus:', error);
    throw error;
  }
};

// Delete potential course from SelectedCourse table
export const deletePotentialCourse = async (courseName) => {
  try {
    const response = await authenticatedFetch(`${API_BASE_URL}/delete-potential-course`, {
      method: 'DELETE',
      body: JSON.stringify({
        course_name: courseName
      }),
    });

    if (!response.ok) {
      throw new Error(`API request failed with status ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error deleting potential course:', error);
    throw error;
  }
}; 